/**
 * Manages the reactive base64 -> merge -> Firebase upload pipeline and
 * exposes `handleOnSave` as the entry point.
 * @module useUploadPipeline
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { getDocumentNameForStatusAndDocType } from "../../../../constants/statusToDocMap/statusToDocMap";
import { useFileToBase64 } from "../../../../hooks/useFileToBase64";
import { useMergePdfs } from "../../../../hooks/useMergePdfs";
import { uploadFileToFirebaseStorage } from "../../../../redux/slice/fileUploadSlice";
import { closeModal } from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";

interface UseUploadPipelineParams {
  orderId: string;
  uploadedFiles: File[];
  authStatus: string;
  docType: string;
  onClose: () => void;
}

/**
 * Drives the 3-step upload pipeline:
 *   1. Convert files to base64
 *   2. Merge into a single PDF
 *   3. Upload to Firebase Storage
 *
 * @param params.orderId - PA order ID used in the storage path.
 * @param params.uploadedFiles - Files selected by the user.
 * @param params.authStatus - Auth status for document naming.
 * @param params.docType - Document type for naming.
 * @param params.onClose - Callback when saving with no files.
 * @returns Pipeline state and the `handleOnSave` trigger.
 */
export const useUploadPipeline = ({
  orderId,
  uploadedFiles,
  authStatus,
  docType,
  onClose,
}: UseUploadPipelineParams) => {
  const [isUploading, setIsUploading] = useState(false);
  const isMergingRef = useRef(false);
  const dispatch = useDispatch<AppDispatch>();

  const { progress, downloadURL } = useSelector(
    (state: RootState) => state.fileUpload,
  );

  const uuid = useMemo(() => uuidv4(), []);
  const fileUploadPath = useMemo(
    () => `medical_pa_order/${orderId}/j_code_docs/${uuid}.pdf`,
    [orderId, uuid],
  );

  const { base64Urls, convertFilesToBase64, clearBase64Urls } = useFileToBase64(
    undefined,
    false,
  );
  const { mergedFile, mergePdfsFromBase64, isMerging } = useMergePdfs();

  useEffect(() => {
    const mergePdfs = async () => {
      if (
        base64Urls.length > 0 &&
        isUploading &&
        !mergedFile &&
        !isMergingRef.current
      ) {
        isMergingRef.current = true;
        const docName = getDocumentNameForStatusAndDocType(authStatus, docType);
        const fileName = `${docName}.pdf`;

        try {
          await mergePdfsFromBase64(base64Urls, fileName);
          clearBase64Urls();
        } catch {
          toast.error(ToastMessages.NAR_LETTER_UPLOAD_ERROR);
          setIsUploading(false);
          isMergingRef.current = false;
          clearBase64Urls();
        }
      }
    };

    mergePdfs();

    return () => {
      if (!mergedFile && isMergingRef.current) {
        isMergingRef.current = false;
      }
    };
  }, [
    base64Urls,
    isUploading,
    mergedFile,
    authStatus,
    docType,
    mergePdfsFromBase64,
    clearBase64Urls,
  ]);

  useEffect(() => {
    if (mergedFile && isUploading) {
      dispatch(uploadFileToFirebaseStorage(mergedFile, fileUploadPath));
    }
  }, [mergedFile, isUploading, dispatch, fileUploadPath]);

  const handleOnSave = async () => {
    if (uploadedFiles.length > 0) {
      setIsUploading(true);
      try {
        await convertFilesToBase64(uploadedFiles);
      } catch {
        toast.error(ToastMessages.NAR_LETTER_UPLOAD_ERROR);
        setIsUploading(false);
        isMergingRef.current = false;
      }
    } else {
      dispatch(closeModal());
      onClose();
    }
  };

  return {
    isUploading,
    setIsUploading,
    isMerging,
    isMergingRef,
    progress,
    fileUploadPath,
    downloadURL,
    mergedFile,
    handleOnSave,
  };
};
